import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faPlus,
  faEdit,
  faTrash,
  faPlay,
  faSearch,
  faTh,
  faList,
  faUpload,
  faClock,
  faCheckCircle,
  faLink,
  faChartLine,
  faEye,
  faFileText,
  faDatabase,
  faFileAlt,
  faBookmark,
  faTimesCircle,
  faSyncAlt,
  faHistory
} from '@fortawesome/free-solid-svg-icons';

import MainLayout from '../Layout/MainLayout';
import { agentAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import CreateAgentModal from './CreateAgentModal';

const AgentManagement = () => {
  const [agents, setAgents] = useState([]);
  const [discoveredAgents, setDiscoveredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadAgents();
    discoverAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await agentAPI.getAgents();
      if (response.success) {
        setAgents(response.data);
      }
    } catch (error) {
      console.error('Failed to load agents:', error);
      setAgents([]);
    }
  };

  const discoverAgents = async () => {
    try {
      setLoading(true);
      // Load agents from discovery API (database)
      const response = await fetch('/api/agents/discovery', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const agentList = data.navigation?.agents?.children || [];
        setDiscoveredAgents(agentList);
        console.log('📊 [DEBUG] Discovered agents:', agentList);
      } else {
        console.error('Failed to discover agents:', response.status);
        setDiscoveredAgents([]);
      }
    } catch (error) {
      console.error('Error discovering agents:', error);
      setDiscoveredAgents([]);
    } finally {
      setLoading(false);
    }
  };


  const handleExecuteAgent = async (agentId) => {
    try {
      const response = await agentAPI.executeAgent(agentId, {});
      if (response.success) {
        toast.success('Agent execution started successfully');
        loadAgents(); // Refresh list
      }
    } catch (error) {
      toast.error('Failed to execute agent');
      console.error('Agent execution error:', error);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) {
      return;
    }

    try {
      const response = await agentAPI.deleteAgent(agentId);
      if (response.success) {
        toast.success('Agent deleted successfully');
        loadAgents(); // Refresh list
      }
    } catch (error) {
      toast.error('Failed to delete agent');
      console.error('Agent deletion error:', error);
    }
  };

  const refreshAgents = async () => {
    setLoading(true);
    await discoverAgents();
    await loadAgents();
  };

  const handleAgentClick = (agent) => {
    if (agent.agent_id) {
      navigate(`/agents/${agent.agent_id}`);
    }
  };

  const handleCreateAgent = async (agentData) => {
    try {
      const response = await agentAPI.createAgent(agentData);
      if (response.success) {
        toast.success('Agent created successfully!');
        setShowCreateModal(false);
        // Refresh both discovered and regular agents
        await refreshAgents();
      }
    } catch (error) {
      toast.error('Failed to create agent. Please try again.');
      console.error('Agent creation error:', error);
      throw error; // Re-throw to let modal handle loading state
    }
  };

  // Icon mapping for agent icons
  const iconMapping = {
    'upload': faUpload,
    'clock': faClock,
    'check-circle': faCheckCircle,
    'x-circle': faTimesCircle,
    'link': faLink,
    'chart-line': faChartLine,
    'eye': faEye,
    'file-text': faFileText,
    'database': faDatabase,
    'file-chart': faFileAlt,
    'bookmark': faBookmark,
    'bot': faRobot,
    'receipt': faFileText,
    'chart-bar': faChartLine,
    'file-invoice': faFileAlt,
    'shopping-cart': faBookmark
  };

  const getCardColor = (index) => {
    const colors = [
      'from-blue-500 to-cyan-500',
      'from-purple-500 to-pink-500',
      'from-green-500 to-teal-500',
      'from-orange-500 to-red-500',
      'from-indigo-500 to-purple-500',
      'from-yellow-500 to-orange-500'
    ];
    return colors[index % colors.length];
  };

  const filteredAgents = (agents || []).filter(agent =>
    agent?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredDiscoveredAgents = (discoveredAgents || []).filter(agent =>
    agent?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAgentIcon = (type) => {
    switch (type) {
      case 'integration':
        return '🔗';
      case 'processing':
        return '⚙️';
      case 'analytics':
        return '📊';
      default:
        return '🤖';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <MainLayout title="Agent Management">
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading agents...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Agent Management">
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            {user?.role === 'admin' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn btn-primary"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Create Agent
              </button>
            )}
            <button
              onClick={refreshAgents}
              className="btn btn-secondary"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSyncAlt} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Agents
            </button>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-sm" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-64"
                placeholder="Search agents..."
              />
            </div>

            {/* View Toggle */}
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-l-lg transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-indigo-500 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faTh} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-r-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-indigo-500 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faList} />
              </button>
            </div>
          </div>
        </div>

        {/* Discovered Agents Section */}
        {(filteredDiscoveredAgents || []).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Discovered Agents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDiscoveredAgents.map((agent, index) => (
                <motion.div
                  key={agent.agent_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                  onClick={() => handleAgentClick(agent)}
                  className="cursor-pointer h-full"
                >
                  <div className="card p-6 hover:shadow-xl transition-all duration-300 border-l-4 border-indigo-500 h-full flex flex-col">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className={`w-14 h-14 flex-shrink-0 rounded-xl bg-gradient-to-r ${getCardColor(index)} flex items-center justify-center text-white shadow-lg`}>
                        <FontAwesomeIcon
                          icon={iconMapping[agent.icon] || faRobot}
                          className="text-xl"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 truncate">
                          {agent.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                          {agent.description || 'AI Agent for automated tasks'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-gray-700">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Active
                      </span>
                      <div className="flex items-center text-indigo-600 dark:text-indigo-400">
                        <span className="text-sm font-medium mr-2">Open</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Regular Agents Grid/List */}
        {(filteredAgents || []).length > 0 && (
          <>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-6">Installed Agents</h3>
          </>
        )}
        {(filteredAgents || []).length > 0 && (
          <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}`}>
            {filteredAgents.map((agent, index) => (
              <motion.div
                key={agent.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`card hover:shadow-lg transition-all duration-200 ${viewMode === 'list' ? 'p-4' : 'p-6'}`}
              >
                {viewMode === 'grid' ? (
                  // Grid View
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-xl">
                          {getAgentIcon(agent.type)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{agent.name}</h3>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)}`}>
                            {agent.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                      {agent.description}
                    </p>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Last execution:</span>
                        <span className="text-gray-900 dark:text-white">{formatTimeAgo(agent.last_execution)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Total runs:</span>
                        <span className="text-gray-900 dark:text-white">{agent.executions_count || 0}</span>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleExecuteAgent(agent.id)}
                        className="btn btn-primary flex-1 text-sm py-2"
                        disabled={agent.status !== 'active'}
                      >
                        <FontAwesomeIcon icon={faPlay} className="mr-1" />
                        Execute
                      </button>
                      <button className="btn btn-secondary px-3 py-2">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        onClick={() => handleDeleteAgent(agent.id)}
                        className="btn btn-danger px-3 py-2"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ) : (
                  // List View
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white">
                        {getAgentIcon(agent.type)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">{agent.name}</h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm">{agent.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)}`}>
                        {agent.status}
                      </span>
                      <div className="text-sm text-gray-500">
                        {formatTimeAgo(agent.last_execution)}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleExecuteAgent(agent.id)}
                          className="btn btn-primary text-sm px-3 py-1"
                          disabled={agent.status !== 'active'}
                        >
                          <FontAwesomeIcon icon={faPlay} />
                        </button>
                        <button className="btn btn-secondary text-sm px-3 py-1">
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button 
                          onClick={() => handleDeleteAgent(agent.id)}
                          className="btn btn-danger text-sm px-3 py-1"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Empty State - shown when no agents exist */}
        {(filteredDiscoveredAgents || []).length === 0 && (filteredAgents || []).length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faRobot} className="text-4xl text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No agents found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchTerm ? 'No agents match your search criteria.' : 'No agents have been discovered yet. Agents are automatically discovered from the database.'}
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={refreshAgents}
                className="btn btn-secondary"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSyncAlt} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh Discovery
              </button>
              {user?.role === 'admin' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn btn-primary"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Create Agent
                </button>
              )}
            </div>
          </div>
        )}

        {/* Create Agent Modal */}
        {showCreateModal && (
          <CreateAgentModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateAgent}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default AgentManagement;